import type { RouteFactory } from '@/routing/types'
import { Outlet, redirect } from 'react-router-dom'
import { MedChronAssetRouteSegments, MedChronAssetTabs } from '@/medical-chronology/asset/constants'
import MedChronEventsTable from '@/medical-chronology/events/components/MedChronEventsTable'
import { MedChronEventsTableVariant } from '@/medical-chronology/events/components/med-chron-events-table-variant'
import MedChronAssetRoot from '@/medical-chronology/asset/components/MedChronAssetRoot'
import MedChronAssetLayout from '@/medical-chronology/asset/components/MedChronAssetLayout'
import AddFilesPage from '@/medical-chronology/asset/components/add-files/AddFilesPage'
import SourceFilesTab from '@/medical-chronology/asset/components/source-files/SourceFilesTab'
import LegacyReportsPage from '@/medical-chronology/asset/components/legacy-reports/LegacyReportsPage'
import BrandAccessGuard from '@/components/guards/brand-access-guard'
import { BrandEnum } from '@/firebase/auth/auth-jwt-schema'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'

/**
 * Creates chronology routes for nesting under MatterLayout.
 * URL pattern: /dashboard/m/:matterId/medical-chronology/...
 *
 * The living chronology rides the new user-files pipeline: every ingestion path is
 * user_file-id based (uploads via useDriveUpload, user_file_ids in the asset API
 * payloads) with no legacy GCS fallback, so without NEW_FILES_UX the feature's data
 * sources do not exist and the routes are not registered at all. The HAIKU brand does
 * not have the pipeline either — gate the routes like timeline/billing-summaries (the
 * sidenav hides the entry behind the same two checks).
 *
 * The index is the matter's living chronology (one per matter). Pre-cutover reports are
 * download-only under legacy-reports; the legacy job viewer is gone (hard cutover).
 *
 * @see MatterLayout for parent route context
 */
export const createChronologyChildRoutes: RouteFactory = () => (kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX) ? createBrandGatedChronologyRoutes() : [])

const createBrandGatedChronologyRoutes: RouteFactory = () => [
  {
    path: 'medical-chronology',
    element: <BrandAccessGuard excludedBrands={[BrandEnum.HAIKU]} />,
    children: [
      {
        element: (
          <MedChronAssetRoot>
            <Outlet />
          </MedChronAssetRoot>
        ),
        children: [
          // The living chronology (tabbed shell)
          {
            element: <MedChronAssetLayout />,
            children: [
              { index: true, loader: () => redirect(MedChronAssetTabs.ALL_EVENTS) },
              { path: MedChronAssetTabs.ALL_EVENTS, element: <MedChronEventsTable variant={MedChronEventsTableVariant.ALL_EVENTS} /> },
              { path: MedChronAssetTabs.DETAILED_VIEW, element: <MedChronEventsTable variant={MedChronEventsTableVariant.DETAILED_VIEW} /> },
              { path: MedChronAssetTabs.SOURCE_FILES, element: <SourceFilesTab /> },
            ],
          },
          // Stage files and generate/update the chronology
          { path: MedChronAssetRouteSegments.ADD_FILES, element: <AddFilesPage /> },
          // Download-only access to pre-cutover chronology reports
          { path: MedChronAssetRouteSegments.LEGACY_REPORTS, element: <LegacyReportsPage /> },
          // Hard cutover: legacy job deep links land on the living chronology
          { path: '*', loader: () => redirect('..') },
        ],
      },
    ],
  },
]
